import classNames from 'classnames';
import React, { useLayoutEffect, useRef, useState } from 'react';
import Breadcrumb from './Breadcrumb';
import * as styles from './Breadcrumbs.module.scss';
import getPotentialElementWidth from '../utils/getPotentialElementWidth';

export type BreadcrumbItem = { name: React.ReactNode; to?: string };

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  size?: 'small' | 'regular';
  truncate?: boolean;
  maximumAllowedSpace?: number;
  showLastCaret?: boolean;
}

const Breadcrumbs = React.forwardRef<HTMLOListElement, BreadcrumbsProps>(
  (
    {
      items,
      size = 'regular',
      truncate = false,
      maximumAllowedSpace,
      showLastCaret,
    },
    ref,
  ) => {
    const [truncationState, setTruncationState] = useState<
      'measuring' | 'expanded' | 'collapsed'
    >(truncate ? 'measuring' : 'expanded');

    const listRef = useRef<HTMLOListElement | null>(null);

    useLayoutEffect(() => {
      const listEl = listRef.current;
      if (!listEl) return undefined;

      if (!truncate || items.length < 3) {
        setTruncationState('expanded');
        return undefined;
      }

      // we indirectly use this component inside containers that are scaled down
      // so we need to account for that too
      const scale = listEl.firstElementChild
        ? listEl.firstElementChild.getBoundingClientRect().width /
          listEl.firstElementChild.clientWidth
        : 1;

      const firstChildWidth =
        listEl.firstElementChild instanceof HTMLElement
          ? getPotentialElementWidth(listEl.firstElementChild) * scale
          : 0;
      const lastChildWidth =
        listEl.lastElementChild instanceof HTMLElement
          ? getPotentialElementWidth(listEl.lastElementChild) * scale
          : 0;

      // simple heuristic to determine how much minimum space we expect for each middle breadcrumb
      const breadcrumbsMinWidth =
        parseInt(getComputedStyle(listEl).fontSize, 10) * 3;

      const expectedMiddleItemsMinWidth =
        (items.length - 2) * breadcrumbsMinWidth * scale;

      const expectedMinWidth =
        firstChildWidth + expectedMiddleItemsMinWidth + lastChildWidth;

      const recalculateTruncationState = () => {
        const containerWidth =
          // we can assume that maximumAllowedSpace is already scaled
          maximumAllowedSpace ?? listEl.getBoundingClientRect().width;
        const shouldCollapse = expectedMinWidth > containerWidth;
        setTruncationState(shouldCollapse ? 'collapsed' : 'expanded');
      };

      recalculateTruncationState();

      const observer = new ResizeObserver(recalculateTruncationState);
      observer.observe(listEl);

      return () => {
        observer.disconnect();
      };
    }, [items.length, truncate, maximumAllowedSpace]);

    const first = items[0];
    const middle = items.slice(1, items.length - 1);
    const last = items[Math.max(items.length - 1, 1)];

    return (
      <ol
        ref={(el) => {
          if (typeof ref === 'function') {
            ref(el);
          } else if (ref) {
            ref.current = el;
          }
          listRef.current = el;
        }}
        className={classNames(styles.container, {
          [styles.small]: size === 'small',
          [styles.truncate]: truncate,
        })}
      >
        {first && (
          <Breadcrumb
            truncate={truncate}
            priority={truncate && items.length >= 2 ? 'high' : undefined}
            hideCaret={last == null && !showLastCaret}
            maximumAllowedContainerSpace={maximumAllowedSpace}
            {...first}
          />
        )}

        {truncationState !== 'measuring' &&
          middle.map((item, index) => (
            <Breadcrumb
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              truncate={truncate}
              collapsed={index === 0 && truncationState === 'collapsed'}
              {...item}
            />
          ))}

        {last && (
          <Breadcrumb
            truncate={truncate}
            priority={truncate ? 'max' : undefined}
            maximumAllowedContainerSpace={maximumAllowedSpace}
            hideCaret={!showLastCaret}
            {...last}
          />
        )}
      </ol>
    );
  },
);

export default Breadcrumbs;
